<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      <span class="text-h5">{{
        $t("admin.productsDatabase.productView.viewProduct")
      }}</span>
      <v-btn
        v-if="!hideCloseButton"
        icon
        @click="$emit('close')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-row>
        <v-col cols="6">
          <p>
            {{ $t("admin.productsDatabase.productView.reviewPublication") }}
          </p>
          <p class="text-body-2">
            {{ $t("admin.productsDatabase.productView.publicationDisplay") }}
          </p>

          <v-row>
            <v-col cols="4">
              <book-cover-image
                :src="bookDetails.image"
                :alt="$t('bookInfo.bookCoverAlt', { title: bookDetails.title })"
                class="rounded-lg"
                contain
                height="120"
              />
            </v-col>
            <v-col cols="8">
              <h4>{{ bookDetails.title }}</h4>
              <v-row>
                <v-col>
                  <div class="text-body-2">
                    <template v-for="(author, index) in bookDetails.authors">
                      <span :key="index">
                        {{ author.name }}
                        <span class="font-italic">({{ author.role }})</span>
                        <span v-if="index < bookDetails.authors.length - 1">,
                        </span>
                      </span>
                    </template>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-chip
                    class="mt-2"
                    color="green"
                    outlined
                  >
                    <v-icon left>
                      mdi-calendar
                    </v-icon>
                    Scheduled: {{ bookDetails.scheduledDate }}
                  </v-chip>
                </v-col>
              </v-row>
              <p class="mt-2 font-weight-bold">
                <Price
                  :book-price="bookDetails.price"
                  :base-currency="bookDetails.currency"
                  :show-in-base-currency="true"
                />
              </p>
              <p class="text-body-2">
                {{ bookDetails.description }}
              </p>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="6">
          <v-row v-if="!hideEditButton">
            <v-col>
              <PrimaryButton @click="editBook">
                {{
                  $t("common.edit")
                }}
              </PrimaryButton>
              <v-btn
                v-if="isLocalBook"
                color="error"
                class="ml-2"
                @click="showDeleteDialog = true"
              >
                {{ $t("admin.productsDatabase.productView.deleteBook") }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <BookDetails :book="book" />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>

    <v-dialog
      v-model="showDeleteDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          {{ $t("admin.productsDatabase.productView.confirmDeleteTitle", { title: bookDetails.title }) }}
        </v-card-title>
        <v-card-text>
          <p>{{ $t("admin.productsDatabase.productView.confirmDeleteText") }}</p>
          <v-text-field
            v-model="deleteConfirmInput"
            :label="$t('admin.productsDatabase.productView.typeDeleteToConfirm')"
            outlined
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showDeleteDialog = false; deleteConfirmInput = ''"
          >
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            :disabled="deleteConfirmInput.toLowerCase() !== 'delete'"
            :loading="deleting"
            @click="deleteBook"
          >
            {{ $t("button.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import BookCoverImage from "@/components/BookCoverImage";
import BookDetails from "@/components/BookDetails.vue";
import PrimaryButton from "@/components/buttons/PrimaryButton.vue";
import Price from "@/components/Price.vue";
import currencyMixins from "@/mixins/currencyMixins";
import { isLocalBook } from "@/types/books";
import { RELEASE_TYPES } from '@/utils/Const';
import LocalBookMutations from "@/graphql/LocalBookMutations";

export default {
  components: {
    BookCoverImage,
    BookDetails,
    PrimaryButton,
    Price,
  },
  mixins: [currencyMixins],
  props: {
    book: {
      type: Object,
      default: () => ({}),
    },
    hideEditButton: {
      type: Boolean,
      default: false,
    },
    hideCloseButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "edit", "deleted"],
  data() {
    return {
      showDeleteDialog: false,
      deleteConfirmInput: '',
      deleting: false,
      bookDetails: {
        image: this.book?.imageUrl || this.book?.thumbnailUrl || "",
        title: this.book?.title || "",
        authors: this.formatAuthors(this.book?.bookAuthors) || [],
        scheduledDate: "Not scheduled",
        price: this.book?.price || 0,
        currency: this.book?.currency?._id || "KES",
        description: this.book?.description || "",
        details: {
          Title: this.book?.title || "",
          Published: this.book?.published || "",
          Publisher: this.book?.publisher?.name || "",
          Language: this.book?.language || "EN",
          Format: this.book?.format?._id?.toUpperCase() || "",
        },
      },
    };
  },
  computed: {
    isLocalBook() {
      return isLocalBook(this.book);
    },
  },
  watch: {
    book: {
      handler(newBook) {
        if (newBook) {
          this.bookDetails = {
            image: newBook.imageUrl || newBook.thumbnailUrl || "",
            title: newBook.title || "",
            authors: this.formatAuthors(newBook.bookAuthors),
            price: newBook.price || 0,
            currency: newBook.currency?._id || "KES",
            description: newBook.description || "",
            scheduledDate: newBook.availableFrom
              ? new Date(newBook.availableFrom).toLocaleDateString()
              : "Not scheduled",
            details: {
              Title: newBook.title || "",
              Published: newBook.createdAt
                ? new Date(newBook.createdAt).getFullYear()
                : "",
              Publisher: newBook.publisher?.name || "",
              Language: newBook.language || "EN",
              Format: newBook.format?._id?.toUpperCase() || "",
            },
          };
        }
      },
      immediate: true,
    },
  },
  methods: {
    editBook() {
      this.$emit("edit", this.book);
    },
    async deleteBook() {
      if (!this.book?.id) return;
      this.deleting = true;
      try {
        await this.$apollo.mutate({
          mutation: LocalBookMutations.deleteLocalBook,
          variables: { id: this.book.id },
        });
        this.showDeleteDialog = false;
        this.deleteConfirmInput = '';
        this.$emit('deleted');
      } catch (error) {
        this.$store.dispatch('snackbar/showSnackbar', {
          text: error.message || this.$t('errors.general'),
          color: 'error',
        });
      } finally {
        this.deleting = false;
      }
    },
    statusColor(status) {
      switch (status) {
        case RELEASE_TYPES.UNLISTED:
          return "grey";
        case RELEASE_TYPES.SCHEDULED:
          return "blue";
        case RELEASE_TYPES.LISTED:
          return "green";
        default:
          return "primary";
      }
    },
    formatAuthors(bookAuthors) {
      if (!this.isLocalBook) {
        return (
          this.book.authors?.map((name) => ({
            name,
            role: "Author",
          })) || []
        );
      }

      if (!bookAuthors) return [];
      return bookAuthors.map((author) => ({
        name: author.author.name,
        role: author.role.description || "Author",
      }));
    },
  },
};
</script>

<style scoped>
.v-img {
  border-radius: 8px;
}
</style>
